import React, { useEffect, useState, useContext } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  MenuItem,
  Stack,
  Table,
  TableBody,
  IconButton,
  TableCell,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { Visibility, Edit, Delete } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import contrattoApi from '../../api/contrattoApi';
import tipoContrattoApi from '../../api/tipocontrattoApi';
import userApi from '../../api/userApi';
import pagamentoApi from '../../api/pagamentoApi';
import ProtectedAction from '../../components/ProtectedAction';
import { AuthContext } from '../../auth/AuthContext';
import ConfirmDialog from '../../components/ConfirmDeleteModal';
import BackButtonDesktop from '../../components/BackButtonDesktop';

const statoContrattoColor = {
  ATTIVO: 'success',
  IN_ATTESA_PAGAMENTO: 'warning',
  SCADUTO: 'default',
  ANNULLATO: 'error'
};

export default function ContrattoEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [form, setForm] = useState(null);
  const [tipiContratto, setTipiContratto] = useState([]);
  const [utente, setUtente] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasFatturate, setHasFatturate] = useState(false);
  const [storicoCanoni, setStoricoCanoni] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedPagamentoId, setSelectedPagamentoId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [contrattoRes, tipiRes] = await Promise.all([
          contrattoApi.get(id),
          tipoContrattoApi.getAll()
        ]);

        const contrattoData = contrattoRes.data;
        setForm(contrattoData);
        setTipiContratto(tipiRes.data.filter(t => t.attivo));
        setHasFatturate(contrattoData.scadenze?.some(s => s.fatturata));
        setStoricoCanoni(contrattoData.storicoCanoni || []);

        if (contrattoData.utenteId) {
          const u = await userApi.get(contrattoData.utenteId);
          setUtente(u.data);
        }
      } catch (e) {
        setErr(e.response?.data || e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return null;
  if (!form) return <Alert severity="error">Contratto non trovato</Alert>;

  const isAnnullato = !!form.dataAnnullamento;
  const isReadOnly = isAnnullato || hasFatturate;

  const handleTipoContrattoChange = (tipoId) => {
    const tipo = tipiContratto.find(t => t.id === tipoId);
    setForm({
      ...form,
      tipocontrattoId: tipoId,
      canoneAnnuo: tipo ? tipo.canoneAnnuo : form.canoneAnnuo
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await contrattoApi.update(id, form);
      navigate('/users/' + utente.id);
    } catch (e) {
      setErr(e.response?.data || e.message);
    }
  };

  const annullaContratto = async () => {
    if (!window.confirm('Annullare definitivamente il contratto?')) return;
    try {
      await contrattoApi.update(id, {
        ...form,
        dataAnnullamento: new Date().toISOString().substring(0, 10),
        attivo: false
      });
      navigate('/contratti');
    } catch (e) {
      setErr(e.response?.data || e.message);
    }
  };

    /* ===================== DELETE ===================== */
    const confirmDelete = (pagId) => {
      setSelectedPagamentoId(pagId);
      setOpenDelete(true);
    };

    const handleDelete = async () => {
      try {
        await pagamentoApi.delete(selectedPagamentoId);
        const contrattoRes = await contrattoApi.get(id);
        const contrattoData = contrattoRes.data;
        setForm(contrattoData);
      } catch (e) {
        console.error('Errore eliminazione pagamento', e);
        setError('Errore eliminazione pagamento');
      } finally {
        setOpenDelete(false);
        setSelectedPagamentoId(null);
      }
    };

        const rinnova = async () => {
        setErr(null);
          //if (!window.confirm('Sei sicuro di voler rinnovare il contratto?')) return;
          try {
            await contrattoApi.renew(id);
            navigate(`/contratti/${id}`)
          } catch (e) {
            setErr(e.response?.data || e.message);
          } finally {
          }
        };
        const scaduto = new Date(form.dataFine) < new Date();
  /* ===================== RENDER ===================== */
  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!form) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Caricamento contratto…</Typography>
      </Container>
    );
  }
  return (
    <Container>
    <BackButtonDesktop />
      <Typography variant="h5" sx={{ mb: 2 }}>
        Modifica Contratto
      </Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{String(err)}</Alert>}

      {isAnnullato && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Contratto annullato il {form.dataAnnullamento}
        </Alert>
      )}

      {hasFatturate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Non è possibile modificare i dati principali perché il contratto ha scadenze fatturate
        </Alert>
      )}

      <Chip
        label={form.stato}
        color={statoContrattoColor[form.stato] || 'default'}
        sx={{ mb: 2 }}
      />
      <form onSubmit={submit}>
        <TextField
          select
          fullWidth
          label="Tipo Contratto"
          value={form.tipocontrattoId}
          onChange={e => handleTipoContrattoChange(e.target.value)}
          sx={{ my: 1 }}
          disabled={isReadOnly}
          required
        >
          {tipiContratto.map(t => (
            <MenuItem key={t.id} value={t.id}>
              {t.tipo} - {t.canoneAnnuo} €
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          type="date"
          label="Data Inizio"
          InputLabelProps={{ shrink: true }}
          value={form.dataInizio}
          onChange={e => setForm({ ...form, dataInizio: e.target.value })}
          sx={{ my: 1 }}
          disabled={isReadOnly}
          required
        />

        <TextField
          fullWidth
          type="date"
          label="Data Fine"
          InputLabelProps={{ shrink: true }}
          value={form.dataFine}
          onChange={e => setForm({ ...form, dataFine: e.target.value })}
          sx={{ my: 1 }}
          disabled={isReadOnly}
          required
        />

        <TextField
          fullWidth
          type="number"
          label="Canone Annuo"
          value={form.canoneAnnuo}
          onChange={e => setForm({ ...form, canoneAnnuo: e.target.value })}
          sx={{ my: 1 }}
          disabled={isReadOnly}
          required
        />

        {utente && (
          <TextField
            fullWidth
            label="Utente"
            value={`${utente.username} - ${utente.email}`}
            sx={{ my: 1 }}
            disabled
          />
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          {!isReadOnly && !isAnnullato && (
            <ProtectedAction user={user} roles={['ADMIN']}>
              <Button type="submit" variant="contained">
                Salva Modifiche
              </Button>
              <Button
                color="error"
                variant="outlined"
                onClick={annullaContratto}
              >
                Annulla Contratto
              </Button>
              {!scaduto && user.role === "ADMIN" && (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={rinnova}
                >
                  Rinnova contratto
                </Button>
              )}
            </ProtectedAction>
          )}
        </Stack>
      </form>

      {storicoCanoni.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>Storico Canoni</Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tipo Contratto</TableCell>
                <TableCell>Canone Annuo</TableCell>
                <TableCell>Da</TableCell>
                <TableCell>A</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {storicoCanoni.map((s, idx) => (
                <TableRow key={idx}>
                  <TableCell>{s.tipoContratto}</TableCell>
                  <TableCell>{s.canoneAnnuo}</TableCell>
                  <TableCell>{s.dataInizio}</TableCell>
                  <TableCell>{s.dataFine || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    <Typography variant="h6" sx={{ mb: 1 }}>
      Pagamenti
    </Typography>
      <ProtectedAction user={user} roles={['ADMIN']}>
        <Button
          variant="contained"
          onClick={() => navigate(`/pagamenti/create?contrattoId=${form.id}`)}
        >
          NUOVO PAGAMENTO
        </Button>
      </ProtectedAction>
    {(!form.pagamenti || form.pagamenti.length === 0) && (
      <Typography color="text.secondary">
        Nessun pagamento presente
      </Typography>
    )}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Importo</TableCell>
            <TableCell>Stato</TableCell>
            <TableCell>Metodo</TableCell>
            <TableCell>Riferimento Esterno</TableCell>
            <TableCell>Data Pagamento</TableCell>
            <TableCell>Azioni</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {form.pagamenti.map((p, idx) => (
            <TableRow key={idx}>
              <TableCell>{p.importo} €</TableCell>
              <TableCell>{p.stato}</TableCell>
              <TableCell>{p.metodo}</TableCell>
              <TableCell>{p.riferimentoEsterno}</TableCell>
              <TableCell>{p.dataPagamento || '-'}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={1}>
                  <ProtectedAction user={user} contractState={user.statoContratto}>
                  <IconButton color="secondary" size="small" onClick={() => navigate(`/pagamenti/edit/${p.id}`)}>
                    <Edit />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => confirmDelete(p.id)}>
                    <Delete />
                  </IconButton></ProtectedAction>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
              <ConfirmDialog
                open={openDelete}
                onCancel={() => setOpenDelete(false)}
                onConfirm={handleDelete}
                title="Elimina Pagamento"
                message="Sei sicuro di voler eliminare questo pagamento?"
              />
    </Container>
  );
}
